<template>
  <v-card :class="[
    'mx-auto w-100 price-card d-flex flex-column pa-3 overflow-visible position-relative border-sm',
    isPopular ? 'price-card--popular' : 'price-card--standard'
  ]" rounded="lg">
    <!-- Popular Badge -->
    <div v-if="isPopular" class="d-flex justify-center mt-n6 mb-2">
      <v-chip color="brand-color" density="compact" label rounded="lg" variant="flat"
        class="text-white text-body-small font-weight-medium price-card__badge">
        🔥 Most Popular
      </v-chip>
    </div>
    <!-- Card Header | Title & Price & Current Chip-->
    <v-card-title :class="[
      'd-flex flex-column align-start justify-center flex-shrink-0 px-4 py-1 text-left w-100',
      isPopular ? '' : 'mt-1'
    ]">
      <div class="d-flex align-center justify-space-between w-100">
        <span class="d-flex align-center ga-1">
          <span class="font-weight-bold text-uppercase text-left plan-name text-grey-darken-1">
            {{ plan.name }}
          </span>
          <v-tooltip v-if="plan.suitability" location="top" max-width="300">
            <template v-slot:activator="{ props }">
              <v-icon v-bind="props" size="14" color="grey-darken-1" icon="mdi-information-slab-circle"
                class="cursor-pointer" />
            </template>
            <span class="text-white font-weight-medium text-tooltip">{{ plan.suitability }}.</span>
          </v-tooltip>
        </span>
        <v-chip v-if="showTrialChip" :color="trialChipColor" density="compact" rounded="lg" variant="flat"
          class="text-white text-body-small font-weight-medium flex-shrink-0 py-2 in-trial-chip">
          {{ trialChipText }}
        </v-chip>
        <v-chip v-else-if="isCurrent && subscriptionStatus !== SUBSCRIPTION_STATUS.NONE" :color="currentPlanChipColor"
          density="compact" rounded="lg" variant="flat"
          class="text-white text-body-small font-weight-medium flex-shrink-0 py-2 current-plan-chip">
          {{ currentPlanChipText }}
        </v-chip>
      </div>
      <div class="d-flex align-baseline w-100 price-card__pricing">
        <span class="font-weight-bold text-grey-darken-4 plan-price text-left mr-2">
          ${{ plan.price }}
        </span>
        <span class="text-grey-darken-3 ma-0 plan-price-interval text-left">
          / month
        </span>
      </div>
      <p class="text-grey-darken-1 ma-0 mt-n2 plan-billed text-left">
        Billed {{ interval === 'month' ? 'monthly' : 'yearly' }}
      </p>
    </v-card-title>

    <!-- Header Divider -->
    <v-divider class="mb-4" color="blue-grey-lighten-1"></v-divider>

    <!-- Features Section -->
    <v-card-text class="px-2 py-0 flex-shrink-0">
      <v-list class="pa-0 bg-transparent plan-features">
        <v-list-item v-for="(feature, index) in plan.cardText" :key="index" :class="['pa-0 mb-2']" min-height="auto">
          <template v-slot:prepend>
            <span class="mx-2" :class="getFeatureIncluded(feature) ? 'text-success' : 'text-error'">
              {{ getFeatureIncluded(feature) ? '✔' : '✘' }}
            </span>
          </template>
          <v-list-item-title class="text-grey-darken-2 font-weight-regular plan-features-item">
            {{ getFeatureLabel(feature) }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-card-text>

    <!-- Spacer to push button to bottom -->
    <div class="flex-grow-1"></div>

    <!-- Action Button -->
    <v-card-actions v-if="!shouldHideButton" class="pa-4 py-0 flex-shrink-0">
      <v-btn :color="buttonColor" variant="flat" block class="text-none text-white font-weight-bold" elevation="0"
        @click="$emit('select-plan', plan)">
        {{ buttonText }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { SUBSCRIPTION_STATUS } from '@/shared/constants/pricing/data'

export default {
  name: 'PCard',
  data() {
    return {
      SUBSCRIPTION_STATUS
    }
  },
  props: {
    plan: { type: Object, required: true },
    interval: { type: String, default: 'month' },
    isCurrent: { type: Boolean, default: false },
    isPopular: { type: Boolean, default: false },
    subscriptionStatus: { type: String, default: SUBSCRIPTION_STATUS.NONE },
    currentSubscriptionStatus: { type: String, default: SUBSCRIPTION_STATUS.NONE },
    isPastDue: { type: Boolean, default: false },
    isTrialingThisPlan: { type: Boolean, default: false },
    isTrialExpiredOnThisPlan: { type: Boolean, default: false }
  },
  computed: {
    // Trials only ever run on the monthly plan - never show the chip on a yearly card,
    // even for the tier currently being trialed.
    showTrialChip() {
      return (this.isTrialingThisPlan || this.isTrialExpiredOnThisPlan) && this.interval === 'month'
    },
    // "In Trial" while it's still running; once it's ended, say so plainly instead of
    // still claiming to be in trial - same red used for a cancelled plan's chip.
    trialChipText() {
      return this.isTrialExpiredOnThisPlan ? 'Trial Ended' : 'In Trial'
    },
    trialChipColor() {
      return this.isTrialExpiredOnThisPlan ? 'error' : 'info-color'
    },
    // Check if button should be hidden (paused/cancelled/past due and not current)
    shouldHideButton() {
      if (this.subscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE || this.isPastDue) {
        return true
      }

      const blockedStatuses = [SUBSCRIPTION_STATUS.PAUSED, SUBSCRIPTION_STATUS.DELETED, SUBSCRIPTION_STATUS.PAST_DUE]
      // Hide button if status is blocked (or user is past due) and not current plan
      return (blockedStatuses.includes(this.subscriptionStatus) || this.isPastDue) && !this.isCurrent
    },
    // Get button text
    buttonText() {
      if (!this.isCurrent || this.subscriptionStatus === SUBSCRIPTION_STATUS.NONE) {
        return 'Choose Plan'
      }

      const labels = {
        [SUBSCRIPTION_STATUS.PAUSED]: 'Manage Paused Plan',
        [SUBSCRIPTION_STATUS.DELETED]: 'Manage Cancelled Plan'
      }

      return labels[this.subscriptionStatus] || 'Manage Plan'
    },
    // Dark navy whenever this is the only visible button (paused/cancelled/past-due hides
    // every other card's button); light info blue whenever this card is the current plan
    // and in good standing - active subscription, or (new) trialing with no subscription yet.
    buttonColor() {
      if (!this.isCurrent) {
        return this.plan.color
      }

      const blockedStatuses = [SUBSCRIPTION_STATUS.PAUSED, SUBSCRIPTION_STATUS.DELETED, SUBSCRIPTION_STATUS.PAST_DUE]
      return blockedStatuses.includes(this.subscriptionStatus) ? this.plan.color : 'info'
    },
    // Shows the current plan badge text for active, paused, cancelled, or past-due states.
    currentPlanChipText() {
      const labels = {
        [SUBSCRIPTION_STATUS.PAUSED]: 'Paused Plan',
        [SUBSCRIPTION_STATUS.DELETED]: 'Cancelled Plan',
        [SUBSCRIPTION_STATUS.PAST_DUE]: 'Past Due'
      }

      return labels[this.subscriptionStatus] || 'Current Plan'
    },
    // Matches the current plan badge color to the subscription status.
    currentPlanChipColor() {
      const colors = {
        [SUBSCRIPTION_STATUS.PAUSED]: 'accent-color',
        [SUBSCRIPTION_STATUS.DELETED]: 'error',
        [SUBSCRIPTION_STATUS.PAST_DUE]: 'accent-color'
      }

      return colors[this.subscriptionStatus] || 'info'
    }
  },
  methods: {
    getFeatureIncluded(feature) {
      if (feature && typeof feature === 'object') {
        return feature.included !== false
      }
      return true
    },
    getFeatureLabel(feature) {
      return typeof feature === 'string' ? feature : feature?.label || ''
    }
  }
}
</script>
